// Third-party libraries
import React, { useContext, useEffect, useState } from "react";
import { Link as DomLink, useHistory, useLocation } from "react-router-dom";
import { ReactMarkdown } from "react-markdown/lib/react-markdown";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import remarkGfm from "remark-gfm";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Container,
  Card,
  CardHeader,
  CardContent,
  Button,
  Typography,
  TextField,
  Stack,
  Divider,
} from "@mui/material";

// Local modules
import { AppContext } from "../../utils/stateHandlers/contexts";
import api from "../../utils/api";

const shouldUseApiClient = process.env.REACT_APP_USE_API_CLIENT !== "false";
const loginRequest = shouldUseApiClient ? api.postRequest : api.post;

const schema = yup.object().shape({
  email: yup
    .string()
    .test("email-not-entered", "Email is required.", (value) => value !== "")
    .required("Email is required."),
  password: yup
    .string()
    .test(
      "password-not-entered",
      "Password is required.",
      (value) => value !== ""
    )
    .required("Password is required."),
});

const POST_LOGIN_KEY = "postLoginSendTo";
const LOGIN_PATH = "/login";
const FALLBACK_PATH = "/";

const isInternalPath = (p) =>
  typeof p === "string" && p.startsWith("/") && !p.startsWith("//");

export default function Login(props) {
  const { user } = useContext(AppContext);
  const [text, setText] = useState();
  const [title, setTitle] = useState();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const sendTo = location.state?.sendTo;
    if (isInternalPath(sendTo) && sendTo !== LOGIN_PATH) {
      sessionStorage.setItem(POST_LOGIN_KEY, sendTo);
    }
  }, [location.state]);

  function redirectAfterLogin() {
    const {
      sendTo: rawSendTo,
      mtoConfiguratorProduct,
      ...restStateFromLogin
    } = location.state || {};

    let sendTo = (
      rawSendTo ||
      sessionStorage.getItem(POST_LOGIN_KEY) ||
      ""
    ).trim();

    const target =
      isInternalPath(sendTo) &&
      sendTo !== location.pathname &&
      sendTo !== LOGIN_PATH
        ? sendTo
        : FALLBACK_PATH;

    sessionStorage.removeItem(POST_LOGIN_KEY);

    const nextState = {
      ...restStateFromLogin,
      ...(mtoConfiguratorProduct ? { mtoConfiguratorProduct } : {}),
    };

    history.replace(
      target,
      Object.keys(nextState).length ? nextState : undefined
    );
  }

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  async function login() {
    const credentials = {
      username: email,
      password: password,
    };

    try {
      const res = await api.postRequest("account/login", credentials);
      user.signIn(res.data, res.data.token);
      redirectAfterLogin();
    } catch (error) {
      const errorData = error.response?.data;
      const errorMessages =
        typeof errorData === "string"
          ? errorData
          : Array.isArray(errorData)
          ? errorData.join(", ")
          : errorData
          ? Object.values(errorData).flat().join(", ")
          : null;

      toast.error(
        errorMessages || "An unexpected error occurred. Please try again."
      );
    }
  }

  useEffect(() => {
    let isMounted = true;

    const fetchContent = async () => {
      try {
        const response = await api.fetchStrapi("/log-in?populate=*");
        if (!isMounted) return;

        const { attributes } = response.data.data;
        setText(attributes.text);
        setTitle(attributes.title);
      } catch (error) {
        if (!isMounted) return;
        console.error("Failed to load login content.", error);
      }
    };

    fetchContent();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Container maxWidth="md">
      <Card>
        <CardHeader
          title={<Typography variant="articleTitle">{title}</Typography>}
        />
        <CardContent>
          <form onSubmit={handleSubmit(login)}>
            <Stack spacing={3}>
              <Typography variant="articleText" sx={{ marginBottom: "1rem" }}>
                <ReactMarkdown children={text} remarkPlugins={[remarkGfm]} />
              </Typography>
              <Divider />
              <Typography>
                <strong>Don't have an account?</strong> Click the button below.
              </Typography>
              <DomLink
                to={{
                  pathname: `/create-account`,
                }}
              >
                <Button variant="contained" color="success">
                  Create New Account
                </Button>
              </DomLink>
              <Divider />
              <Stack>
                <Typography variant="navText">Email</Typography>
                <TextField
                  {...register("email")}
                  error={!!errors["email"]}
                  helperText={errors["email"] ? errors["email"].message : ""}
                  required
                  autoFocus
                  color="primary"
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setValue("email", e.target.value, { shouldValidate: true });
                  }}
                  onKeyDown={(e) => e.key === "Enter" && login()}
                  size="small"
                  sx={{ width: "100%", margin: "unset" }}
                />
              </Stack>
              <Stack>
                <Typography variant="navText">Password</Typography>
                <TextField
                  {...register("password")}
                  error={!!errors["password"]}
                  helperText={
                    errors["password"] ? errors["password"].message : ""
                  }
                  required
                  color="primary"
                  type="password"
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setValue("password", e.target.value, {
                      shouldValidate: true,
                    });
                  }}
                  onKeyDown={(e) => e.key === "Enter" && login()}
                  size="small"
                  sx={{ width: "100%", margin: "unset" }}
                />
              </Stack>
            </Stack>
            <Button
              size="small"
              color="primary"
              variant="contained"
              type="submit"
              sx={{ marginTop: "20px" }}
            >
              Log in
            </Button>
            <Stack spacing={1} marginTop={"10px"}>
              <Typography variant="subtitle2">
                <DomLink to={{ pathname: "/forgot-password" }}>
                  Forgot Password?
                </DomLink>
              </Typography>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
}
